/**
 * Every AI provider an enterprise can connect, in the order a run tries them: each vendor's own API first, then
 * OpenRouter, which can route to any of them.
 */
export const AGENT_PROVIDERS = ['claude', 'openai', 'grok', 'openrouter'] as const

export type AgentProvider = (typeof AGENT_PROVIDERS)[number]

/** Whether a stored value names a provider an enterprise can connect. */
export function isAgentProvider(value: unknown): value is AgentProvider {
    return (AGENT_PROVIDERS as readonly unknown[]).includes(value)
}

export type AgentConnectionTarget = {
    layer: 'enterprise' | 'member'
    layerId: string
    name: string
}

/** Credential metadata only. Connected means stored and readable, not provider-verified. */
export type AgentConnection = {
    id: string
    provider: AgentProvider
    layer: AgentConnectionTarget['layer']
    layerId: string
    ownerName: string
    authKind: 'api_key' | 'oauth' | null
    status: 'connected' | 'unavailable'
    /** Lower values are tried first among connections for the same provider and owner. */
    priority: number
    updatedAt: string | null
    /**
     * What this credential is, in the operator's words — "Design team Max seat", "Billing fallback".
     * Provider and owner alone do not separate several subscriptions of the same provider. Null until
     * someone names it; the UI falls back to the provider name.
     */
    name: string | null
    description: string | null
    /**
     * The seat a subscription login bills (Claude Pro/Max, ChatGPT, SuperGrok), for pricing its runs as a share of
     * the plan rather than at API rates. Null for API keys and OpenRouter, and for a login whose plan nobody has
     * detected or set yet.
     */
    subscription: AgentSubscription | null
}

/**
 * The plans a subscription login can be on, per provider. `team` / `business` / `enterprise` / `custom` have no list
 * price: the operator types one in. OpenRouter has no subscription.
 */
export const AGENT_PROVIDER_SUBSCRIPTION_PLANS = {
    claude: ['pro', 'max_5x', 'max_20x', 'team', 'enterprise', 'custom'],
    openai: ['plus', 'chatgpt_pro', 'business', 'enterprise', 'custom'],
    grok: ['supergrok', 'supergrok_heavy', 'custom'],
    openrouter: [],
} as const satisfies Record<AgentProvider, readonly string[]>

export type AgentSubscriptionPlan = (typeof AGENT_PROVIDER_SUBSCRIPTION_PLANS)[AgentProvider][number]

/** Every plan of every provider. A connection takes only its own provider's (`AGENT_PROVIDER_SUBSCRIPTION_PLANS`). */
export const AGENT_SUBSCRIPTION_PLANS: readonly AgentSubscriptionPlan[] = [
    ...new Set<AgentSubscriptionPlan>(Object.values(AGENT_PROVIDER_SUBSCRIPTION_PLANS).flat()),
]

/** Monthly list price in cents for the plans that have one. */
export const AGENT_SUBSCRIPTION_PRICES: Partial<Record<AgentSubscriptionPlan, number>> = {
    pro: 2000,
    max_5x: 10000,
    max_20x: 20000,
    plus: 2000,
    chatgpt_pro: 20000,
    supergrok: 3000,
    supergrok_heavy: 30000,
}

export type AgentSubscription = {
    plan: AgentSubscriptionPlan
    /** What the seat costs a month, in cents. Null when the plan is known but has no list price and none was set. */
    monthlyUsdCents: number | null
    /** `detected` came from the provider's own account; `operator` was set by hand and is never overwritten by detection. */
    source: 'detected' | 'operator'
}

export const AGENT_CONNECTION_NAME_MAX = 80
export const AGENT_CONNECTION_DESCRIPTION_MAX = 280

export type AgentConnectionsResponse = {
    connections: AgentConnection[]
    targets: AgentConnectionTarget[]
}

export type AgentUsageKind = 'subscription' | 'balance' | 'unavailable'

export type AgentUsageQuota = {
    id: string
    label: string
    usedPercent: number
    resetsAt: string | null
}

export type AgentUsageDay = {
    date: string
    usdCents: number
}

export type AgentUsageBalance = {
    remainingCents: number | null
    usedCents: number | null
    limitCents: number | null
    usedPercent: number | null
    todayCents: number | null
    weekCents: number | null
    monthCents: number | null
    averageDailyCents: number | null
    daysRemaining: number | null
    days: AgentUsageDay[]
    /**
     * The last four characters of the stored key, taken from the credential the server holds and never from the
     * provider; null when the key is under 16 characters or unknown. The only key material the API ever returns.
     * There is deliberately no field for the provider's own key label: OpenRouter's, unless renamed, is a shortened
     * copy of the key itself (#1773), and naming the contract keeps a provider string from being passed through.
     */
    keyLastFour?: string | null
    /**
     * How often the key's own limit resets (OpenRouter's `limit_reset`: at midnight UTC, on Mondays, or on the 1st),
     * or null when it never does. A spent periodic limit comes back at the next reset; a spent balance does not.
     */
    limitReset?: 'daily' | 'weekly' | 'monthly' | null
}

/** Provider usage for one agent connection. Never includes credentials. */
export type AgentUsage = {
    kind: AgentUsageKind
    quotas: AgentUsageQuota[]
    balance: AgentUsageBalance | null
    message: string | null
}

/**
 * The most connection ids one `GET /v1/llm/connections/usage?ids=…` names. The API refuses more with
 * `agent_connection_ids_limit`; the portal splits a longer list into requests of at most this many.
 */
export const AGENT_CONNECTION_USAGE_IDS_MAX = 100

/**
 * What `GET /v1/llm/connections/usage?ids=…` answers: each requested connection's usage, keyed by its id, the same
 * `AgentUsage` its own `/connections/:id/usage` answers (OS-PORTAL-2). An id the caller's enterprise does not hold is
 * left out; a connection whose usage could not be read comes back `unavailable`.
 */
export type AgentUsageByConnection = Record<string, AgentUsage>
